<script setup lang="ts">
import {
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  type Maybe,
  unwrapMaybeOr,
} from "../lib/maybe";
import { computed, watch, type WritableComputedRef } from "vue";
import { assertNever } from "../lib/assertNever";
import {
  DATABASE_ENTITY_TYPES,
  type EntityFrontmatter,
} from "../lib/entityFrontmatter";
import {
  entityEffectiveColor,
  normalizeEntityColor,
} from "../lib/entityColors";
import { displayFileName } from "../lib/fileNames";
import { readBoolPref, writeBoolPref } from "../lib/localPrefs";
import { useTea } from "../lib/tea";
import {
  DATABASE_FIELD_KINDS,
  newDatabaseFieldId,
  type ZwDatabaseFieldDef,
  type ZwDatabaseFieldKind,
} from "../lib/zwConfig";
import { useActiveEntityFieldsStore } from "../stores/useActiveEntityFieldsStore";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import { useProjectSettingsStore } from "../stores/useProjectSettingsStore";
import { useProjectWorkspaceStore } from "../stores/useProjectWorkspaceStore";
import EntityColorPicker from "./EntityColorPicker.vue";
import {
  initialEntityFieldsPanelDialogsModel,
  updateEntityFieldsPanelDialogsFn,
  type EntityFieldsPanelDialogsCmd,
} from "./EntityFieldsPanel.update";
import ZwButton from "./ui/ZwButton.vue";
import ZwDialog from "./ui/ZwDialog.vue";
import ZwInput from "./ui/ZwInput.vue";
import ZwMenu from "./ui/ZwMenu.vue";
import ZwMenuItem from "./ui/ZwMenuItem.vue";
import ZwMenuSeparator from "./ui/ZwMenuSeparator.vue";
import ZwSelect from "./ui/ZwSelect.vue";
import ZwTagList from "./ui/ZwTagList.vue";
import ZwTextarea from "./ui/ZwTextarea.vue";

const session = useEditorSessionStore();
const workspace = useProjectWorkspaceStore();
const projectSettings = useProjectSettingsStore();
const entityFields = useActiveEntityFieldsStore();

/** Visible only for a real worldbuilding entry — never a map (no frontmatter
 * to read) and never any other file kind. */
const visible = computed(
  () => isJust(session.activeDatabase) && session.activeMap === false,
);

const fields = computed(() => entityFields.fields);
const customFieldDefs = computed<ZwDatabaseFieldDef[]>(() =>
  matchMaybe(session.projectConfig, {
    just: (config) => config.databaseFields,
    nothing: () => [],
  }),
);

function setField<K extends keyof EntityFrontmatter>(
  key: K,
  value: EntityFrontmatter[K],
  options?: { immediate?: boolean },
): void {
  entityFields.setField(key, value, options);
}

/** Attributes explicitly added via "Add attribute" this session but not yet
 * given a value. Reset on every entry switch via ClearAddedAttrKeys. */

function maybeStringField(
  get: () => Maybe<string>,
  key: "type" | "role" | "summary" | "status" | "plural" | "adjective",
  options?: { immediate?: boolean },
): WritableComputedRef<string> {
  return computed<string>({
    get: () => unwrapMaybeOr(get(), ""),
    set: (v: string) => {
      const trimmed = v.trim();
      setField(key, trimmed.length > 0 ? just(trimmed) : nothing, options);
    },
  });
}

const typeValue = maybeStringField(() => fields.value.type, "type", {
  immediate: true,
});
const roleValue = maybeStringField(() => fields.value.role, "role");
const summaryValue = maybeStringField(() => fields.value.summary, "summary");
const statusValue = maybeStringField(() => fields.value.status, "status");
const pluralValue = maybeStringField(() => fields.value.plural, "plural");
const adjectiveValue = maybeStringField(
  () => fields.value.adjective,
  "adjective",
);
const akaValue = computed<string[]>({
  get: () => fields.value.aka,
  set: (v) => setField("aka", v, { immediate: true }),
});
const tagsValue = computed<string[]>({
  get: () => fields.value.tags,
  set: (v) => setField("tags", v, { immediate: true }),
});

/** The entry's title *is* its filename — exactly like chapters/non-default
 * scenes (`SceneTitleBar.vue`'s `titleValue`/`titleDraft`/`commitTitle`,
 * mirrored here): no separate stored "name" field to drift out of sync or
 * race a reload. Renames on blur/Enter, matching that same precedent — a
 * failed rename (e.g. a collision) already alerts from
 * `onRenameProjectDatabase` and just leaves the file where it was, and the
 * `watch` below resets the draft back to the real title either way. */
const titleValue = computed(() =>
  matchMaybe(session.activeDatabase, {
    just: (activeFile) => displayFileName(activeFile),
    nothing: () => "",
  }),
);

function onColorChange(value: Maybe<string>): void {
  const normalized = normalizeEntityColor(value);
  setField("color", normalized, { immediate: true });
}

/** Sentinel select value that opens the custom-type dialog instead of
 * committing directly — never itself written to `fields.type`. */
const CUSTOM_TYPE_OPTION = "__custom__";

const builtInTypeOptions = [
  { value: "", label: "Untyped" },
  ...DATABASE_ENTITY_TYPES.map((t) => ({ value: t.id, label: t.label })),
];

/** The current type, plus a "Custom…" trigger — and, when the entry's type
 * is already some earlier author-entered custom string, that value too, so
 * the select reflects it instead of silently showing "Untyped". */
const typeOptions = computed(() => {
  const current = fields.value.type;
  const isCustomCurrent =
    isJust(current) &&
    current.value.length > 0 &&
    DATABASE_ENTITY_TYPES.some((t) => t.id === current.value) === false;
  return [
    ...builtInTypeOptions,
    ...(isCustomCurrent
      ? [{ value: current.value, label: current.value }]
      : []),
    { value: CUSTOM_TYPE_OPTION, label: "Custom…" },
  ];
});

const typeLabel = computed(
  () =>
    typeOptions.value.find((o) => o.value === typeValue.value)?.label ??
    "Untyped",
);

async function runDialogsCmd(cmd: EntityFieldsPanelDialogsCmd): Promise<void> {
  switch (cmd.kind) {
    case "applyType":
      typeValue.value = cmd.value;
      return;
    case "addField": {
      const def: ZwDatabaseFieldDef = {
        id: newDatabaseFieldId(),
        name: cmd.name,
        kind: cmd.fieldKind,
        options: [...cmd.options],
      };
      await projectSettings.addDatabaseField(def);
      void dispatchDialogs({
        type: "AddAttrKey",
        key: `custom:${def.id}`,
      });
      return;
    }
    case "persistFieldsOpen":
      writeBoolPref("entityFieldsOpen", cmd.open);
      return;
    default:
      assertNever(cmd);
  }
}

const { model: dialogsModel, dispatch: dispatchDialogs } = useTea(
  initialEntityFieldsPanelDialogsModel(readBoolPref("entityFieldsOpen")),
  updateEntityFieldsPanelDialogsFn,
  { runCmd: just(runDialogsCmd) },
);

watch(
  () => session.activeDatabase,
  () => {
    void dispatchDialogs({ type: "ClearAddedAttrKeys" });
  },
);

watch(
  titleValue,
  (value) => {
    void dispatchDialogs({ type: "SeedTitle", title: value });
  },
  { immediate: true },
);

const titleDraft = computed({
  get: (): string => dialogsModel.value.titleDraft,
  set: (draft: string): void => {
    void dispatchDialogs({ type: "TitleDraftChanged", draft });
  },
});

const fieldsOpen = computed({
  get: (): boolean => dialogsModel.value.fieldsOpen,
  set: (open: boolean): void => {
    void dispatchDialogs({ type: "SetFieldsOpen", open });
  },
});

const addedAttrKeys = computed(() => dialogsModel.value.addedAttrKeys);

function commitTitle(): void {
  const activeFile = session.activeDatabase;
  if (isNothing(activeFile)) return;
  const to = titleDraft.value.trim();
  if (to.length === 0 || to === titleValue.value) {
    void dispatchDialogs({ type: "SeedTitle", title: titleValue.value });
    return;
  }
  const name = activeFile.value;
  const extIndex = name.lastIndexOf(".");
  const ext = extIndex >= 0 ? name.slice(extIndex) : "";
  void workspace.onRenameProjectDatabase(name, `${to}${ext}`);
}

const customTypeDialogOpen = computed(() => dialogsModel.value.customType.open);
const customTypeDraft = computed({
  get: (): string => dialogsModel.value.customType.draft,
  set: (draft: string): void => {
    void dispatchDialogs({
      type: "CustomType",
      msg: { type: "DraftChanged", draft },
    });
  },
});

function onTypeSelect(value: string): void {
  if (value !== CUSTOM_TYPE_OPTION) {
    typeValue.value = value;
    return;
  }
  const current = fields.value.type;
  const draft =
    isJust(current) &&
    current.value.length > 0 &&
    DATABASE_ENTITY_TYPES.some((t) => t.id === current.value) === false
      ? current.value
      : "";
  void dispatchDialogs({
    type: "CustomType",
    msg: { type: "Open", draft },
  });
}

function commitCustomType(): void {
  void dispatchDialogs({ type: "CustomType", msg: { type: "Commit" } });
}

function cancelCustomType(): void {
  void dispatchDialogs({ type: "CustomType", msg: { type: "Cancel" } });
}

/** A single addable/visible attribute — either one of the fixed built-in
 * fields (role/status/aka/tags/summary) or an author-defined custom field.
 * Unified into one shape so "Add attribute", grouping, and rendering don't
 * each need their own builtin/custom branching. */
type AttrKind = ZwDatabaseFieldKind;
type AttrSpec = {
  key: string;
  name: string;
  kind: AttrKind;
  options: string[];
  source: "builtin" | "custom";
  builtinId?:
    "role" | "status" | "plural" | "adjective" | "aka" | "tags" | "summary";
  customDef?: ZwDatabaseFieldDef;
};

/** Fixed render order for whatever attributes end up visible — grouped by
 * kind, not by when each was added. */
const KIND_ORDER: AttrKind[] = ["text", "select", "tags", "longText"];

const BUILT_IN_ATTRS: {
  id: "role" | "status" | "plural" | "adjective" | "aka" | "tags" | "summary";
  name: string;
  kind: AttrKind;
}[] = [
  { id: "role", name: "Role", kind: "text" },
  { id: "status", name: "Status", kind: "text" },
  { id: "plural", name: "Plural", kind: "text" },
  { id: "adjective", name: "Adjective / demonym", kind: "text" },
  { id: "aka", name: "Also known as", kind: "tags" },
  { id: "tags", name: "Tags", kind: "tags" },
  { id: "summary", name: "Summary", kind: "longText" },
];

const attrCatalog = computed<AttrSpec[]>(() => [
  ...BUILT_IN_ATTRS.map((b) => ({
    key: `builtin:${b.id}`,
    name: b.name,
    kind: b.kind,
    options: [] as string[],
    source: "builtin" as const,
    builtinId: b.id,
  })),
  ...customFieldDefs.value.map((d) => ({
    key: `custom:${d.id}`,
    name: d.name,
    kind: d.kind,
    options: d.options,
    source: "custom" as const,
    customDef: d,
  })),
]);

function maybeNonEmpty(m: Maybe<string>): boolean {
  return isJust(m) && m.value.length > 0;
}

function attrHasValue(spec: AttrSpec): boolean {
  if (spec.source === "builtin") {
    switch (spec.builtinId) {
      case "role":
        return maybeNonEmpty(fields.value.role);
      case "status":
        return maybeNonEmpty(fields.value.status);
      case "plural":
        return maybeNonEmpty(fields.value.plural);
      case "adjective":
        return maybeNonEmpty(fields.value.adjective);
      case "aka":
        return fields.value.aka.length > 0;
      case "tags":
        return fields.value.tags.length > 0;
      case "summary":
        return maybeNonEmpty(fields.value.summary);
      default:
        return false;
    }
  }
  const v = fields.value.custom[spec.customDef!.id];
  return Array.isArray(v)
    ? v.length > 0
    : typeof v === "string" && v.length > 0;
}

const visibleAttrs = computed(() => {
  const visible = attrCatalog.value.filter(
    (spec) => attrHasValue(spec) || addedAttrKeys.value.has(spec.key),
  );
  return KIND_ORDER.flatMap((kind) => visible.filter((s) => s.kind === kind));
});

/** Compact (grid-friendly) vs. wide attributes — tags/long text need their
 * own full-width row, short text/select fit two-up. */
const compactAttrs = computed(() =>
  visibleAttrs.value.filter((s) => s.kind === "text" || s.kind === "select"),
);
const wideAttrs = computed(() =>
  visibleAttrs.value.filter((s) => s.kind === "tags" || s.kind === "longText"),
);

const addableAttrs = computed(() => {
  const visibleKeys = new Set(visibleAttrs.value.map((s) => s.key));
  return attrCatalog.value.filter(
    (spec) => visibleKeys.has(spec.key) === false,
  );
});

function addAttribute(spec: AttrSpec): void {
  void dispatchDialogs({ type: "AddAttrKey", key: spec.key });
}

function attrTextValue(spec: AttrSpec): string {
  if (spec.source === "builtin") {
    if (spec.builtinId === "role") return roleValue.value;
    if (spec.builtinId === "status") return statusValue.value;
    if (spec.builtinId === "plural") return pluralValue.value;
    if (spec.builtinId === "adjective") return adjectiveValue.value;
    if (spec.builtinId === "summary") return summaryValue.value;
    return "";
  }
  const v = fields.value.custom[spec.customDef!.id];
  return typeof v === "string" ? v : "";
}
function setAttrTextValue(spec: AttrSpec, value: string): void {
  if (spec.source === "builtin") {
    if (spec.builtinId === "role") roleValue.value = value;
    else if (spec.builtinId === "status") statusValue.value = value;
    else if (spec.builtinId === "plural") pluralValue.value = value;
    else if (spec.builtinId === "adjective") adjectiveValue.value = value;
    else if (spec.builtinId === "summary") summaryValue.value = value;
    return;
  }
  const custom = { ...fields.value.custom };
  if (value.trim().length > 0) custom[spec.customDef!.id] = value;
  else delete custom[spec.customDef!.id];
  // `setAttrTextValue` backs both text (ZwInput, continuous typing — keep
  // debounced) and select (ZwSelect, a discrete pick — flush immediately)
  // compact attrs; see the KIND_ORDER/compactAttrs split above.
  if (spec.kind === "select") {
    setField("custom", custom, { immediate: true });
  } else {
    setField("custom", custom);
  }
}
function attrTagsValue(spec: AttrSpec): string[] {
  if (spec.source === "builtin") {
    if (spec.builtinId === "aka") return akaValue.value;
    if (spec.builtinId === "tags") return tagsValue.value;
    return [];
  }
  const v = fields.value.custom[spec.customDef!.id];
  return Array.isArray(v) ? v : [];
}
function setAttrTagsValue(spec: AttrSpec, value: string[]): void {
  if (spec.source === "builtin") {
    if (spec.builtinId === "aka") akaValue.value = value;
    else if (spec.builtinId === "tags") tagsValue.value = value;
    return;
  }
  const custom = { ...fields.value.custom };
  if (value.length > 0) custom[spec.customDef!.id] = value;
  else delete custom[spec.customDef!.id];
  setField("custom", custom, { immediate: true });
}
function attrSelectOptions(spec: AttrSpec): { value: string; label: string }[] {
  return [
    { value: "", label: "—" },
    ...spec.options.map((o) => ({ value: o, label: o })),
  ];
}

const newAttributeDialogOpen = computed(
  () => dialogsModel.value.newAttribute.open,
);
const newAttributeName = computed({
  get: (): string => dialogsModel.value.newAttribute.name,
  set: (name: string): void => {
    void dispatchDialogs({
      type: "NewAttribute",
      msg: { type: "NameChanged", name },
    });
  },
});
const newAttributeKind = computed({
  get: (): ZwDatabaseFieldKind => dialogsModel.value.newAttribute.kind,
  set: (kind: ZwDatabaseFieldKind): void => {
    void dispatchDialogs({
      type: "NewAttribute",
      msg: { type: "KindChanged", kind },
    });
  },
});
const newAttributeOptionsText = computed({
  get: (): string => dialogsModel.value.newAttribute.optionsText,
  set: (optionsText: string): void => {
    void dispatchDialogs({
      type: "NewAttribute",
      msg: { type: "OptionsTextChanged", optionsText },
    });
  },
});

function openNewAttributeDialog(): void {
  void dispatchDialogs({ type: "NewAttribute", msg: { type: "Open" } });
}

function cancelNewAttribute(): void {
  void dispatchDialogs({ type: "NewAttribute", msg: { type: "Cancel" } });
}

function commitNewAttribute(): void {
  void dispatchDialogs({ type: "NewAttribute", msg: { type: "Commit" } });
}
</script>

<template>
  <div v-if="visible" class="entity-fields-bar">
    <div class="entity-header-row">
      <EntityColorPicker
        :model-value="entityEffectiveColor(fields.color, fields.type)"
        :clearable="isJust(fields.color)"
        @update:model-value="onColorChange"
      />
      <div class="entity-header-text">
        <div class="entity-title-row">
          <ZwInput
            :id="nothing"
            v-model="titleDraft"
            variant="plain"
            class="entity-title-input"
            aria-label="Title"
            placeholder="Untitled"
            :label="nothing"
            @keydown.enter.prevent="commitTitle"
            @blur="commitTitle"
          />
          <button
            type="button"
            class="entity-fields-toggle"
            :aria-expanded="fieldsOpen"
            :aria-label="fieldsOpen ? 'Hide fields' : 'Show fields'"
            :title="fieldsOpen ? 'Hide fields' : 'Show fields'"
            @click="void dispatchDialogs({ type: 'ToggleFieldsOpen' })"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              class="size-3"
              :class="{ 'entity-fields-toggle-open': fieldsOpen }"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="m9 6 6 6-6 6"
              />
            </svg>
          </button>
        </div>
        <ZwMenu
          class="entity-fields-type-menu"
          align="start"
          :menu-label="nothing"
        >
          <template #trigger="{ toggle, attrs }">
            <button
              type="button"
              class="entity-fields-type-summary"
              v-bind="attrs"
              @click="toggle()"
            >
              {{ typeLabel }}
            </button>
          </template>
          <ZwMenuItem
            v-for="option in typeOptions"
            :key="option.value"
            :label="option.label"
            :shortcut="nothing"
            :title="nothing"
            @select="onTypeSelect(option.value)"
          />
        </ZwMenu>
      </div>
    </div>
    <div
      class="entity-fields-collapse"
      :class="{ 'entity-fields-collapse-open': fieldsOpen }"
    >
      <div class="entity-fields-collapse-inner">
        <div class="entity-fields-grid">
          <template v-for="spec in compactAttrs" :key="spec.key">
            <ZwInput
              v-if="spec.kind === 'text'"
              :id="nothing"
              :label="just(spec.name)"
              :model-value="attrTextValue(spec)"
              @update:model-value="setAttrTextValue(spec, $event)"
            />
            <ZwSelect
              v-else
              :id="nothing"
              :label="just(spec.name)"
              :options="just(attrSelectOptions(spec))"
              :model-value="attrTextValue(spec)"
              @update:model-value="setAttrTextValue(spec, $event)"
            />
          </template>
        </div>
        <template v-for="spec in wideAttrs" :key="spec.key">
          <ZwTagList
            v-if="spec.kind === 'tags'"
            :label="spec.name"
            :model-value="attrTagsValue(spec)"
            @update:model-value="setAttrTagsValue(spec, $event)"
          />
          <ZwTextarea
            v-else
            :id="nothing"
            :label="just(spec.name)"
            autosize
            :rows="1"
            :model-value="attrTextValue(spec)"
            @update:model-value="setAttrTextValue(spec, $event)"
          />
        </template>
        <ZwMenu
          class="entity-add-attribute"
          align="start"
          :menu-label="nothing"
        >
          <template #trigger="{ toggle, attrs }">
            <ZwButton size="sm" v-bind="attrs" @click="toggle()">
              + Add attribute
            </ZwButton>
          </template>
          <ZwMenuItem
            v-for="spec in addableAttrs"
            :key="spec.key"
            :label="spec.name"
            :shortcut="nothing"
            :title="nothing"
            @select="addAttribute(spec)"
          />
          <ZwMenuSeparator v-if="addableAttrs.length > 0" />
          <ZwMenuItem
            label="Custom…"
            :shortcut="nothing"
            :title="nothing"
            @select="openNewAttributeDialog"
          />
        </ZwMenu>
      </div>
    </div>

    <ZwDialog
      :open="customTypeDialogOpen"
      title="Custom type"
      :description="nothing"
      @close="cancelCustomType"
    >
      <ZwInput
        :id="nothing"
        v-model="customTypeDraft"
        :label="just('Type')"
        placeholder="Type name"
        @keydown.enter.prevent="commitCustomType"
      />
      <template #footer>
        <ZwButton @click="cancelCustomType">Cancel</ZwButton>
        <ZwButton variant="primary" @click="commitCustomType"
          >Set type</ZwButton
        >
      </template>
    </ZwDialog>

    <ZwDialog
      :open="newAttributeDialogOpen"
      title="New attribute"
      :description="nothing"
      @close="cancelNewAttribute"
    >
      <ZwInput
        :id="nothing"
        v-model="newAttributeName"
        :label="just('Name')"
        @keydown.enter.prevent="commitNewAttribute"
      />
      <ZwSelect
        :id="nothing"
        v-model="newAttributeKind"
        :label="just('Type')"
        :options="
          just(
            DATABASE_FIELD_KINDS.map((k) => ({ value: k.id, label: k.label })),
          )
        "
      />
      <ZwInput
        v-if="newAttributeKind === 'select'"
        :id="nothing"
        v-model="newAttributeOptionsText"
        :label="just('Choices')"
        placeholder="Choices, comma-separated"
        @keydown.enter.prevent="commitNewAttribute"
      />
      <template #footer>
        <ZwButton @click="cancelNewAttribute">Cancel</ZwButton>
        <ZwButton
          variant="primary"
          :disabled="newAttributeName.trim().length === 0"
          @click="commitNewAttribute"
        >
          Add attribute
        </ZwButton>
      </template>
    </ZwDialog>
  </div>
</template>

<style scoped>
.entity-fields-bar {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  box-sizing: border-box;
  width: 100%;
  max-width: 40rem;
  margin-right: auto;
  margin-left: var(--zw-prose-margin-left, 0px);
  padding: var(--space-md) 1.5rem 0;
  border-bottom: 1px solid var(--line);
}

.entity-header-row {
  display: flex;
  align-items: stretch;
  width: 100%;
  gap: 0.75rem;
}

.entity-header-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.entity-title-row {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 0.6rem;
}

.entity-title-input {
  --title-font-size: 1.5rem;
  flex: 1;
  min-width: 0;
}

.entity-title-input :deep(.zw-input),
.entity-title-input :deep(.zw-input-face-text) {
  font-family: var(--font-heading);
  font-size: var(--title-font-size);
  font-weight: 600;
  line-height: 1.25;
}

.entity-fields-type-menu {
  margin: 0 0 var(--space-sm);
}

.entity-fields-type-summary {
  appearance: none;
  border: 0;
  background: transparent;
  margin: 0;
  padding: 0;
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: 0.15rem;
  display: inline-flex;
}

.entity-fields-type-summary:hover,
.entity-fields-type-summary:focus-visible {
  color: var(--text-strong);
}

.entity-fields-type-summary:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 2px;
}

.entity-fields-toggle {
  appearance: none;
  border: 0;
  background: transparent;
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: 0.2rem;
  transition: color var(--duration-fast) var(--ease-out-quart);
}

.entity-fields-toggle:hover {
  color: var(--text-strong);
}

.entity-fields-toggle-open {
  transform: rotate(90deg);
}

.entity-fields-collapse {
  display: grid;
  grid-template-rows: 0fr;
  /* This is itself a flex item of `.entity-fields-bar` — without an explicit
   * `overflow` (and thus a non-`visible` box), a flex item's default
   * `min-height: auto` falls back to its content's min-content size, which
   * for form controls (native `<select>`, bordered inputs) is well above 0.
   * That overrode the `0fr` track below and left a sliver visible even fully
   * collapsed — `.entity-fields-collapse-inner`'s own `overflow: hidden` only
   * fixes automatic-minimum-size for *that* box, one level too deep. */
  overflow: hidden;
  min-height: 0;
  opacity: 0;
  transition: grid-template-rows var(--duration-med) var(--ease-out-quart);
  padding-top: 0.5rem;
}

.entity-fields-collapse-open {
  grid-template-rows: 1fr;
  opacity: 1;
}

.entity-fields-collapse-inner {
  overflow: hidden;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  padding-bottom: var(--space-md);
}

.entity-fields-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-md) var(--space-lg, 1rem);
}

.entity-add-attribute {
  align-self: flex-start;
}

/* Padding on `.entity-fields-collapse-inner` would stop it collapsing to a
 * true 0 height (it's animated via a 0fr/1fr grid track, and padding doesn't
 * shrink with that). Draw focus rings inward instead, so they never need
 * room past each control's own border in the first place. */
.entity-fields-collapse-inner :deep(.zw-input-shell:focus-within),
.entity-fields-collapse-inner :deep(.zw-select:focus) {
  outline-offset: -2px;
}
</style>
